import React from "react";
import { Box, Typography, useTheme } from "@mui/material";

export interface ChoiceOption<K extends string = string> {
  key: K;
  title: string;
  description: string;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface ChoiceTileProps<K extends string = string> {
  option: ChoiceOption<K>;
  onSelect: (key: K) => void;
}

function ChoiceTile<K extends string = string>({
  option,
  onSelect,
}: ChoiceTileProps<K>): React.ReactElement {
  const theme = useTheme();

  return (
    <Box
      onClick={option.disabled ? undefined : () => onSelect(option.key)}
      sx={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        padding: "20px",
        borderRadius: "12px",
        border: `1px solid ${theme.palette.divider}`,
        cursor: option.disabled ? "not-allowed" : "pointer",
        opacity: option.disabled ? 0.5 : 1,
        transition: "border-color 0.15s ease, background-color 0.15s ease",
        "&:hover": {
          borderColor: option.disabled
            ? theme.palette.divider
            : theme.palette.primary.main,
          backgroundColor: option.disabled
            ? "transparent"
            : theme.palette.action.hover,
        },
      }}
    >
      {option.icon && (
        <Box sx={{ color: theme.palette.primary.main }}>{option.icon}</Box>
      )}
      <Typography
        sx={{
          fontSize: "16px",
          fontWeight: 700,
          color: theme.palette.text.primary,
        }}
      >
        {option.title}
      </Typography>
      <Typography
        sx={{
          fontSize: "13px",
          color: theme.palette.text.tertiary,
          lineHeight: 1.5,
        }}
      >
        {option.description}
      </Typography>
    </Box>
  );
}

export default ChoiceTile;
